import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import { ChatCircleText, Trash, PencilSimple, DownloadSimple } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { toastFactory } from '@/factories'

import API from '../../../../api/api'
import Avatar from '../../../../Avatar'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import pdfDownload from '../../../../helpers/pdfDownload'

const MaintenanceRequestPreviewNavbar = ({
  data,
  timezone,
  status,
  setShowCompleteRequestModal,
  setShowCancelRequestModal,
  handleEditMode,
  isEditMode,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const navigate = useNavigate()

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const selectedBuilding = useSelector(selectActiveBuilding)
  const hasDisplayBuildingNumber = selectedBuilding?.displayBuildingNumber

  const { avatar, firstName, lastName, email, phone, buildingNumber, apartment } =
    data.clientId || {}

  const unitNumberToRender =
    hasDisplayBuildingNumber && buildingNumber && apartment
      ? `${buildingNumber} - ${apartment}`
      : apartment

  const goToSpecificChat = () => {
    const rootRoute = '/dashboard/chat'
    const params = {
      buildingId: data?.buildingId?.id,
      channelName: `${data.clientId?.userId}_MTRQ_${data?.id}`,
    }
    const queryString = new URLSearchParams(params).toString()
    navigate(`${rootRoute}?${queryString}`)
  }

  const downloadPdf = async () => {
    if (isDownloadingPdf) return
    setIsDownloadingPdf(true)
    try {
      const response = await API.downloadMaintenanceRequestPDF(data.id)
      pdfDownload(response, `work-order-${data.sourceId || data.id}`)
    } catch {
      toastFactory.error(t('maintenance:requestPreview.navbar.downloadPdfError'))
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  const isDisabled = ['Finished', 'Canceled'].includes(status.text)

  return (
    <div className="maintenance-request-container maintenance-request-preview-navbar">
      <div className="single-row space-between" style={{ margin: '0px 0px' }}>
        <h2>
          {t('maintenance:requestPreview.navbar.heading', {
            sourceId: data.sourceId ? `#${data.sourceId}` : '',
          })}
        </h2>
        {!isEditMode && (
          <div className="icons-wrapper">
            <div
              className={`connect-icon ${isDownloadingPdf ? 'connect-icon__disabled' : ''}`}
              onClick={downloadPdf}
              title={t('maintenance:requestPreview.navbar.downloadPdfTitle')}
            >
              <DownloadSimple size={20} />
            </div>
            <div
              className="connect-icon connect-icon__remove"
              onClick={() => setShowCancelRequestModal(true)}
            >
              <Trash size={20} />
            </div>
            <div className="connect-icon" onClick={goToSpecificChat}>
              <ChatCircleText size={20} />
            </div>
            <div
              className={`connect-icon ${isDisabled ? 'connect-icon__disabled' : ''}`}
              onClick={() => !isDisabled && handleEditMode(true)}
            >
              <PencilSimple size={20} />
            </div>
            <button
              disabled={isDisabled}
              onClick={() => setShowCompleteRequestModal(true)}
              className={`button button-primary `}
            >
              {t('maintenance:requestPreview.completeModal.title')}
            </button>
          </div>
        )}
        {isEditMode && (
          <div className="icons-wrapper">
            <button
              disabled={isDisabled}
              onClick={() => handleEditMode(false)}
              className={`button button-primary is-block`}
            >
              {t('common:actions.cancel')}
            </button>
          </div>
        )}
      </div>
      <div className="single-row">
        <div className="single-block">
          <div className="block-title">{t('maintenance:requestPreview.navbar.requestObject')}</div>
          <div className="user-container">
            {data.clientId ? (
              <React.Fragment>
                <Avatar
                  size="big"
                  style={{ marginRight: '10px' }}
                  square
                  zoomTitle={`${firstName} ${lastName}`}
                  zoomable
                >
                  <img
                    src={getAssetLink({
                      path: 'chitown-avatar/avatar',
                      file: avatar,
                      fallback: { file: 'avatar.png' },
                    })}
                  />
                </Avatar>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="block-contain name">
                    {firstName} {lastName}
                  </div>
                  <div className="block-contain">{email}</div>
                </div>
              </React.Fragment>
            ) : data.unit || data.shortDescription ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="block-contain">{data.unit || data.shortDescription}</div>
              </div>
            ) : (
              <div className="block-contain">{data.location}</div>
            )}
          </div>
        </div>
        {data.clientId && (
          <React.Fragment>
            <div className="single-block">
              <div className="block-title">{t('maintenance:requestPreview.navbar.aptNumber')}</div>
              <div className="block-contain">{unitNumberToRender}</div>
            </div>
            <div className="single-block">
              <div className="block-title">
                {t('maintenance:requestPreview.navbar.phoneNumber')}
              </div>
              <div className="block-contain">{phone}</div>
            </div>
          </React.Fragment>
        )}
        <div className="single-block">
          <div className="block-title">{t('maintenance:requestPreview.navbar.scheduledFor')}</div>
          <div className="block-contain">
            {formatInTimeZone(data.createdAt, timezone, 'MM/dd/yyyy')}
          </div>
        </div>
        <div className="single-block">
          <div className="block-title">{t('maintenance:requestPreview.navbar.status')}</div>
          <div className={`block-status-filter ${data.status.replace(/\s+/g, '')}`}>
            {data.status}
          </div>
        </div>
      </div>
    </div>
  )
}

MaintenanceRequestPreviewNavbar.propTypes = {
  data: PropTypes.object,
  timezone: PropTypes.string,
  status: PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
    id: PropTypes.number,
  }),
  setShowCompleteRequestModal: PropTypes.func,
  setShowCancelRequestModal: PropTypes.func,
  handleEditMode: PropTypes.func,
  isEditMode: PropTypes.bool,
}

export default MaintenanceRequestPreviewNavbar
